<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
    <b-modal
      v-model="isOpen"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-label="ActualitesModal"
      aria-modal
      scroll="keep"
      class="ActualitesModal"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <H3TitleView class="modal-card-title">
            {{ $t("actualitesPage.manage-actualite-type") }}
          </H3TitleView>
        </header>
        <section v-if="actualiteTypes.length > 0" class="modal-card-body">
          <b-field :label="$t('actualitesPage.actualite-type-modal.type-list')">
            <b-select
              v-model="selectedActualiteType"
              expanded
              :placeholder="$t('actualitesPage.actualite-type-modal.placeholder-type')"
            >
              <option
                v-for="type in actualiteTypes"
                :key="type.label"
                class="color-option"
                :style="{
                  '--background-color': type.color,
                  '--text-color': type.color === defaultColorType ? 'black' : 'white',
                }"
                :value="type"
              >
                {{ getActualiteTypeListLabel(type) }}
              </option>
            </b-select>
          </b-field>

          <div v-if="actualiteType" class="mt-2">
            <!-- Titre du type-->
            <InputWithValidation
              v-model="actualiteType.label"
              type="text"
              :label="$t('actualitesPage.actualite-type-modal.type-label')"
              maxlength="30"
              vid="type-label"
              rules="required"
              @manual-change="checkExistingLabel"
            />
            <p v-show="isExistingTypeLabel" class="help is-danger addingErrorMessage">
              {{ $t("actualitesPage.error-type-title") }}
            </p>

            <!-- Couleur du type-->
            <label class="label">
              <FormInputLabel
                :label="$t('actualitesPage.actualite-type-modal.type-color')"
                :is-mandatory="true"
              />
            </label>
            <color-picker v-model="selectedColor" />
            <p v-if="colorErrorMessage" class="help is-danger">{{ colorErrorMessage }}</p>
          </div>
        </section>

        <footer class="modal-card-foot">
          <div class="buttons">
            <div class="hoverable-discrete-link mr-3" @click="emitCloseModale">
              {{ $t("close-generic") }}
            </div>
            <b-button
              v-if="actualiteType && actualiteType.id"
              :label="$t('delete').toString()"
              type="is-danger"
              rounded
              outlined
              @click="deleteType($event)"
            />
            <b-button
              :disabled="
                !actualiteType ||
                !actualiteType.label ||
                !actualiteType.color ||
                isExistingTypeLabel
              "
              :label="$t('save-generic').toString()"
              type="is-primary"
              rounded
              @click="handleSubmit(saveType)"
            />
          </div>
        </footer>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { ActualiteTypeDTO } from "@/model/bean/GeneratedDTOs"
import H3TitleView from "../commons/H3TitleView.vue"
import InputWithValidation from "../inputs/InputWithValidation.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { ActualiteService } from "@/services/rest/actualite/ActualiteService"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import ColorPicker, { DEFAULT_PICKER_COLOR } from "@/views/generic_components/ColorPicker.vue"
import FormInputLabel from "@/views/inputs/FormInputLabel.vue"
import { BButton, BModal, BSelect, BField } from "buefy"

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

export default defineComponent({
  name: "ActualiteTypesModalView",
  components: {
    FormInputLabel,
    ColorPicker,
    H3TitleView,
    InputWithValidation,
    ValidationObserver,
    BButton,
    BField,
    BSelect,
    BModal,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["close"],
  data() {
    return {
      actualiteService: ActualiteService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,

      actualiteTypes: [] as ActualiteTypeDTO[],
      actualiteTypeLabels: [] as string[],
      selectedActualiteType: null as ActualiteTypeDTO | null,
      actualiteType: null as ActualiteTypeDTO | null,
      defaultColorType: DEFAULT_PICKER_COLOR,
      selectedColor: DEFAULT_PICKER_COLOR as any,
      colorErrorMessage: "",
      isExistingTypeLabel: false,
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.$emit("close")
        }
      },
    },
    defaultNewActualiteType(): ActualiteTypeDTO {
      const defaultActualiteType = new ActualiteTypeDTO()
      defaultActualiteType.color = this.defaultColorType
      defaultActualiteType.label = ""
      return defaultActualiteType
    },
  },
  watch: {
    selectedColor(newColor: string): void {
      if (this.actualiteType != null) {
        this.actualiteType.color = newColor
      }
    },
    selectedActualiteType(newValue: ActualiteTypeDTO | null): void {
      if (newValue != null) {
        this.actualiteType = Object.assign({}, newValue)
        this.selectedColor = this.actualiteType?.color || this.defaultColorType
      }
    },
  },
  mounted(): void {
    this.reloadActualiteTypes()
  },
  methods: {
    async reloadActualiteTypes(): Promise<void> {
      this.actualiteTypes = await this.actualiteService.getAllActualiteTypes()
      if (this.actualiteTypes?.length > 0) {
        this.actualiteTypeLabels = this.actualiteTypes.map((type) =>
          this.getActualiteTypeListLabel(type).toLowerCase(),
        )
      }
      this.actualiteTypes.push(this.defaultNewActualiteType)
      this.resetFields()
    },

    updateActualiteType(newValue: ActualiteTypeDTO | null): void {
      if (newValue != null) {
        this.actualiteType = Object.assign({}, newValue)
        this.selectedColor = this.actualiteType?.color || this.defaultColorType
      }
    },

    validateColor(): boolean {
      this.colorErrorMessage = ""

      if (!this.actualiteType?.color) {
        this.colorErrorMessage = "La couleur est obligatoire"
      } else if (!HEX_REGEX.test(this.actualiteType.color)) {
        // Vérifier si la valeur saisie est un code hexadécimal valide
        this.colorErrorMessage =
          "Le format de la couleur n'est pas valide. Veuillez entrer une couleur au format #RRGGBB ou #RGB."
      }

      return this.colorErrorMessage === ""
    },

    getActualiteTypeListLabel(type: ActualiteTypeDTO): string {
      return type.label || this.$t("actualitesPage.actualite-type-modal.add-new-type").toString()
    },

    emitCloseModale(): void {
      this.resetFields()
      this.$emit("close")
    },

    async saveType(): Promise<void> {
      if (this.actualiteType && this.validateColor()) {
        try {
          const savedLabel = this.actualiteType.label
          const messageKeySuffix = this.actualiteType.id == undefined ? "create" : "update"

          // Sauvegarde
          await this.actualiteService.saveActualiteType(this.actualiteType)
          // Sélectionne le type sauvegardé
          await this.reloadActualiteTypes()
          this.updateSelectedTypeByLabel(savedLabel)

          // Affiche le message de succès
          this.infoReportingService.success(
            this.$t("actualitesPage.actualite-type-modal.confirm-" + messageKeySuffix).toString(),
          )
        } catch (e: any) {
          this.infoReportingService.error(this.getErrorStatusText(e.statusText), e as Error)
        }
      }
    },

    updateSelectedTypeByLabel(label: string): void {
      if (!label) {
        this.selectedActualiteType = null
      } else {
        const actualiteType = this.actualiteTypes.find((type) => type.label === label)
        this.selectedActualiteType = actualiteType ? actualiteType : null
      }
    },

    deleteType(event: Event): void {
      event.stopPropagation()
      if (this.actualiteType?.id) {
        const actualiteTypeId = this.actualiteType.id
        this.infoReportingService.dialog(
          this.$t("graePage.confirmation").toString(),
          this.$t("actualitesPage.delete-type").toString(),
          this.$t("actualitesPage.confirm-delete").toString(),
          () => this.doDeleteType(actualiteTypeId),
          "is-danger",
        )
      }
    },

    async doDeleteType(actualiteTypeId: string): Promise<void> {
      try {
        await this.actualiteService.deleteActualiteType(actualiteTypeId)
        await this.reloadActualiteTypes()

        this.infoReportingService.success(
          this.$t("actualitesPage.actualite-type-modal.pop-up-confirm-delete").toString(),
        )
      } catch (e: any) {
        this.infoReportingService.error(this.getErrorStatusText(e.statusText), e as Error)
      }
    },

    getErrorStatusText(errorStatusText: string | undefined): string {
      return errorStatusText ? JSON.parse(errorStatusText)?.error : ""
    },
    resetFields(): void {
      this.selectedActualiteType = null
      this.actualiteType = null
    },

    checkExistingLabel(): void {
      if (this.actualiteType?.label) {
        this.isExistingTypeLabel =
          this.actualiteTypeLabels.includes(this.actualiteType.label.toLowerCase()) &&
          this.selectedActualiteType?.label.toLocaleLowerCase() !=
            this.actualiteType?.label.toLowerCase()
      }
    },
  },
})
</script>

<style scoped lang="scss">
.select select option {
  background-color: var(--background-color, "#f5f5f5");
  color: var(--text-color, black);
}

p.help.is-danger.addingErrorMessage {
  margin-top: -20px;
  margin-bottom: 20px;
}
</style>
